import { getCapturedNotes, markNotesUploaded, type CapturedNote } from './NoteStore.ts';

const UPLOAD_PATH = '/v1/notes/capture';
const DEFAULT_BACKEND_BASE_URL = 'https://api.app.comprehendpt.com';

const SETTLED_UPLOAD_MS = 5 * 60 * 1000;
const DIRTY_UPLOAD_MS = 30 * 60 * 1000;
const RETRY_DELAY_MS = 60 * 1000;
const EMBED_UPLOAD_TIMEOUT_MS = 15 * 1000;

/*
number -> the backend answered with this HTTP status
null   -> there are no credentials to upload with
*/
type UploadStatus = number | null;

type CaptureSession = {
  sessionKey: string | number;
  sessionEmail: string;
  backendBaseUrl?: string;
};

declare global {
  interface Window {
    ComprehendEmbed?: {
      send: (type: string, payload: any) => boolean | undefined;
    };
  }
}

let flushInFlight = false;
let retryAfter = 0;

export function isDue(note: CapturedNote, now = Date.now()) {
  if (note.uploaded) return false;
  if (note.signedOffAt !== undefined) return true;

  const dirtySince = note.dirtySince ?? note.createdAt;
  return (now - note.modifiedAt >= SETTLED_UPLOAD_MS || now - dirtySince >= DIRTY_UPLOAD_MS);
}

export async function flushDueEntries(
  keepalive = false,
  forceCacheKey?: string
) {
  if (
    !keepalive &&
    (flushInFlight || Date.now() < retryAfter)
  ) {
    return;
  }
  if (!keepalive) flushInFlight = true;

  try {
    const store = await getCapturedNotes();
    const now = Date.now();
    const dueEntries = Object.entries(store.entries).filter(([cacheKey, note]) =>
      cacheKey === forceCacheKey || isDue(note, now)
    );
    if (dueEntries.length === 0) return;

    const notes = dueEntries.map(([cacheKey, note]) => {
      const emr = JSON.parse(cacheKey).emr;
      // Keep dueEntries untouched: upload results must match the raw cached snapshot.
      return {
        cacheKey, ...note, emr,
        patientInfo: sanitizeCaptureValue(note.patientInfo),
        pageInfo: sanitizeCaptureValue(note.pageInfo),
        questions: sanitizeCaptureValue(note.questions),
        postWriteQuestions: sanitizeCaptureValue(note.postWriteQuestions),
        initialQuestions: sanitizeCaptureValue(note.initialQuestions),
        initialQuestionsFrozen: note.initialQuestionsFrozen
      };
    });
    const status = await uploadNotes(notes, keepalive);
    if (status === null) return;

    if (status !== 200) {
      retryAfter = Date.now() + RETRY_DELAY_MS;
      return;
    }

    retryAfter = 0;
    await markNotesUploaded(dueEntries);
  } catch (_) {
    retryAfter = Date.now() + RETRY_DELAY_MS;
  } finally {
    if (!keepalive) flushInFlight = false;
  }
}

function sanitizeCaptureValue(value: any): any {
  if (typeof value === 'string') {
    // Meta tags can trigger WAF XSS rules inside otherwise valid JSON.
    return value.replace(/<meta(?=[\s/>])(?:[^<>"']|"[^"]*"|'[^']*')*>/gi, '');
  }
  if (Array.isArray(value)) return value.map(sanitizeCaptureValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, sanitizeCaptureValue(item)])
    );
  }
  return value;
}

function uploadNotes(notes: any[], keepalive: boolean) {
  if (hasExtensionRuntime()) {
    return uploadFromExtension(notes);
  }
  if (window.ComprehendEmbed?.send) {
    return uploadFromEmbed(notes, keepalive);
  }
  return uploadFromWeb(notes, keepalive);
}

// A dedicated port prevents another extension listener from claiming the
// response and leaving successfully uploaded notes marked for retry.
function uploadFromExtension(notes: any[]) {
  return new Promise<UploadStatus>((resolve, reject) => {
    const port = chrome.runtime.connect({ name: 'note-capture-upload' });
    let settled = false;
    port.onMessage.addListener((response: any) => {
      settled = true;
      if (response?.status === null || typeof response?.status === 'number') {
        resolve(response.status);
      } else {
        reject(new Error('note capture upload reply had no status'));
      }
      port.disconnect();
    });
    port.onDisconnect.addListener(() => {
      if (!settled) reject(new Error('note capture upload connection closed'));
    });
    port.postMessage({ notes });
  });
}

function uploadFromEmbed(notes: any[], keepalive: boolean) {
  return new Promise<UploadStatus>((resolve) => {
    const id = `notes_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', handleResponse);
      resolve(0);
    }, EMBED_UPLOAD_TIMEOUT_MS);

    const handleResponse = (event: MessageEvent) => {
      const message = event.data;
      if (
        !message?.__comp ||
        message.type !== 'CAPTURED_NOTES_UPLOAD_RESPONSE' ||
        message.id !== id
      ) {
        return;
      }

      clearTimeout(timeoutId);
      window.removeEventListener('message', handleResponse);
      resolve(Number(message.status) || 0);
    };

    window.addEventListener('message', handleResponse);
    const sent = window.ComprehendEmbed?.send('UPLOAD_CAPTURED_NOTES', {
      id,
      notes,
      keepalive
    });
    if (!sent) {
      clearTimeout(timeoutId);
      window.removeEventListener('message', handleResponse);
      resolve(null);
    }
  });
}

async function uploadFromWeb(notes: any[], keepalive: boolean) {
  const captureSession = getBrowserCaptureSession();
  if (!captureSession) return null;

  const response = await fetch(getUploadUrl(captureSession), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(makeUploadBody(captureSession, notes)),
    keepalive
  });
  return response.status;
}

function getBrowserCaptureSession(): CaptureSession | null {
  try {
    const sessionKey = sessionStorage.getItem('sessionKey') || localStorage.getItem('sessionKey');
    const sessionEmail = sessionStorage.getItem('sessionEmail') || localStorage.getItem('sessionEmail');
    const captureSession = {
      sessionKey: sessionKey || '',
      sessionEmail: sessionEmail || ''
    };
    return isValidCaptureSession(captureSession) ? captureSession : null;
  } catch (_) {
    return null;
  }
}

function isValidCaptureSession(
  captureSession?: CaptureSession | null
): captureSession is CaptureSession {
  return Boolean(
    captureSession?.sessionEmail &&
      captureSession.sessionKey !== null &&
      captureSession.sessionKey !== undefined &&
      Number.isInteger(Number(captureSession.sessionKey))
  );
}

function makeUploadBody(captureSession: CaptureSession, notes: any[]) {
  return {
    sessionKey: Number(captureSession.sessionKey),
    sessionEmail: captureSession.sessionEmail,
    notes
  };
}

function getUploadUrl(captureSession: CaptureSession) {
  const baseUrl = (captureSession.backendBaseUrl || DEFAULT_BACKEND_BASE_URL).replace(/\/$/, '');
  return `${baseUrl}${UPLOAD_PATH}`;
}

function hasExtensionRuntime() {
  return Boolean(typeof chrome !== 'undefined' && chrome.runtime?.id);
}
